import React, { useState } from 'react';
import { useAppStore } from '@/lib/store';
import { uid } from '@/utils/format';
import { WORKER_URL } from '@/lib/convex';

const CONNECTOR_TYPES = [
  { id: 'google-mail', name: 'Google Mail', description: 'Read and search your Gmail inbox', icon: '✉️', available: true },
  { id: 'google-docs', name: 'Google Docs', description: 'Fetch content from Google Docs (public & private)', icon: '📄', available: true },
];

export default function ConnectorsView() {
  const { tools, setTools, connectors, setConnectors } = useAppStore();
  const [activeTab, setActiveTab] = useState('connectors');
  const [googleClientId, setGoogleClientId] = useState('');
  const [docsUrl, setDocsUrl] = useState('');
  const [docsToken, setDocsToken] = useState('');
  const [fetchedDoc, setFetchedDoc] = useState(null);
  const [docsLoading, setDocsLoading] = useState(false);
  const [newToolName, setNewToolName] = useState('');
  const [newToolDesc, setNewToolDesc] = useState('');

  // ─── Google OAuth connect ─────────────────────
  const connectGoogle = (type) => {
    if (!googleClientId.trim()) {
      alert('Enter your Google OAuth Client ID first.');
      return;
    }
    const redirectUri = `${WORKER_URL}/api/connectors/google/callback`;
    const scope = encodeURIComponent(
      type === 'google-mail'
        ? 'https://www.googleapis.com/auth/gmail.readonly'
        : 'https://www.googleapis.com/auth/documents.readonly'
    );
    const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(googleClientId)}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${scope}&access_type=offline&prompt=consent`;
    window.open(url, '_blank', 'width=500,height=600');
  };

  // ─── Fetch Google Doc ─────────────────────────
  const fetchGoogleDoc = async () => {
    if (!docsUrl.trim()) return;
    setDocsLoading(true);
    setFetchedDoc(null);
    try {
      const match = docsUrl.match(/\/d\/([\w-]+)/);
      const docId = match?.[1];
      if (!docId) throw new Error('Could not extract doc ID from URL');
      const headers = { 'Content-Type': 'application/json' };
      if (docsToken.trim()) headers['Authorization'] = `Bearer ${docsToken}`;
      const res = await fetch(`${WORKER_URL}/api/connectors/google/docs?id=${docId}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch document');
      const data = await res.json();
      setFetchedDoc(data);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setDocsLoading(false);
    }
  };

  // ─── Tools ───────────────────────────────────
  const addTool = () => {
    if (!newToolName.trim() || !newToolDesc.trim()) return;
    setTools([...tools, { id: uid(), name: newToolName.trim(), description: newToolDesc.trim(), isActive: true, createdAt: Date.now() }]);
    setNewToolName('');
    setNewToolDesc('');
  };

  const toggleTool = (id) => setTools(tools.map((t) => (t.id === id ? { ...t, isActive: !t.isActive } : t)));
  const removeTool = (id) => setTools(tools.filter((t) => t.id !== id));

  return (
    <div className="flex-1 flex flex-col p-4 md:p-6 overflow-y-auto w-full bg-[#232426]">
      <h1 className="text-white font-bold text-[18px] md:text-xl mb-6">Connectors</h1>

      {/* Tabs */}
      <div className="flex bg-transparent border border-[#515153] rounded-[4px] overflow-hidden text-[13.5px] font-medium h-[30px] mb-8 w-fit">
        {['connectors', 'tools'].map((t) => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`px-5 h-full transition-colors flex items-center justify-center border-r border-[#515153] last:border-r-0 capitalize ${activeTab === t ? 'bg-[#784A15] text-[#ED8B38]' : 'text-[#d5d6d9] hover:bg-[#323335]'}`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ═══ CONNECTORS TAB ═══ */}
      {activeTab === 'connectors' && (
        <div className="flex flex-col gap-6 max-w-3xl">
          {/* Google OAuth Client ID */}
          <div className="bg-[#323335] border border-[#515153] rounded-md p-4">
            <h3 className="text-white font-bold text-[14px] mb-3">Google OAuth Setup</h3>
            <p className="text-[#8f9196] text-[12px] mb-3">
              Enter your Google OAuth Client ID to enable one-click Google integrations. Create a project at{' '}
              <span className="text-[#6498CC]">console.cloud.google.com</span> and enable Gmail + Docs APIs.
            </p>
            <div className="flex gap-3">
              <input
                value={googleClientId}
                onChange={(e) => setGoogleClientId(e.target.value)}
                className="flex-1 bg-[#232426] border border-[#515153] rounded px-3 py-1.5 text-white text-[13px] outline-none focus:border-[#6498CC]"
                placeholder="123456789-abc.apps.googleusercontent.com"
              />
              <button
                onClick={() => { const updated = connectors.filter((c) => c.type !== 'google-oauth'); setConnectors([...updated, { id: uid(), type: 'google-oauth', config: { clientId: googleClientId }, isActive: true, createdAt: Date.now() }]); }}
                className="bg-[#6498CC] hover:bg-[#5282b8] text-white px-4 py-1.5 rounded text-[13px] font-bold transition-all"
              >
                Save
              </button>
            </div>
          </div>

          {/* Connector cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {CONNECTOR_TYPES.map((ct) => {
              const active = connectors.find((c) => c.type === ct.id && c.isActive);
              return (
                <div key={ct.id} className="bg-[#323335] border border-[#515153] rounded-md p-4 flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{ct.icon}</span>
                    <div>
                      <div className="text-white font-bold text-[14px]">{ct.name}</div>
                      <div className="text-[#8f9196] text-[12px]">{ct.description}</div>
                    </div>
                    {active && <span className="ml-auto text-[10px] font-bold bg-[#244c2a] text-[#71e184] px-2 py-0.5 rounded uppercase">Connected</span>}
                  </div>

                  {ct.id === 'google-docs' && (
                    <div className="flex flex-col gap-2">
                      <input
                        value={docsUrl}
                        onChange={(e) => setDocsUrl(e.target.value)}
                        className="w-full bg-[#232426] border border-[#515153] rounded px-3 py-1.5 text-white text-[12px] outline-none focus:border-[#6498CC]"
                        placeholder="https://docs.google.com/document/d/..."
                      />
                      <input
                        value={docsToken}
                        onChange={(e) => setDocsToken(e.target.value)}
                        type="password"
                        className="w-full bg-[#232426] border border-[#515153] rounded px-3 py-1.5 text-white text-[12px] outline-none focus:border-[#6498CC]"
                        placeholder="OAuth token (optional, for private docs)"
                      />
                      <button
                        onClick={fetchGoogleDoc}
                        disabled={docsLoading || !docsUrl.trim()}
                        className="bg-[#6498CC] hover:bg-[#5282b8] text-white px-4 py-1.5 rounded text-[12px] font-bold transition-all disabled:opacity-50"
                      >
                        {docsLoading ? 'Fetching...' : 'Fetch Document'}
                      </button>
                      {fetchedDoc && (
                        <div className="bg-[#232426] border border-[#3e3f42] rounded p-3 text-[12px] text-[#d5d6d9] max-h-40 overflow-y-auto">
                          <div className="text-white font-bold mb-1">{fetchedDoc.title}</div>
                          <div className="text-[#8f9196]">{fetchedDoc.wordCount} words</div>
                          <pre className="whitespace-pre-wrap text-[#d5d6d9] mt-2">{fetchedDoc.text?.slice(0, 500)}{fetchedDoc.text?.length > 500 ? '…' : ''}</pre>
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    onClick={() => connectGoogle(ct.id)}
                    className="mt-auto w-full bg-[#232426] border border-[#515153] hover:border-[#6498CC] text-[#d5d6d9] hover:text-white py-2 rounded text-[13px] font-medium transition-all flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" /></svg>
                    {active ? 'Reconnect' : 'Connect'} with Google
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══ TOOLS TAB ═══ */}
      {activeTab === 'tools' && (
        <div className="flex flex-col gap-5 max-w-3xl">

          {/* Built-in system tools */}
          <div className="bg-[#323335] border border-[#515153] rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-[#515153] flex items-center gap-2">
              <span className="text-[#71e184] text-[12px] font-bold uppercase tracking-wider">Built-in System Tools</span>
              <span className="text-[10px] bg-[#244c2a] text-[#71e184] font-bold px-2 py-0.5 rounded">Always Active</span>
            </div>
            {[
              { icon: '📅', name: 'calendar.add', desc: 'Schedule any event, meeting, or reminder with a date and time. Triggered by phrases like "remind me", "schedule", "add to calendar".' },
              { icon: '🗑️', name: 'calendar.remove', desc: 'Remove a calendar event by title. Triggered when user says "cancel", "delete", or "remove" an event.' },
              { icon: '✏️', name: 'calendar.update', desc: 'Reschedule or update an existing calendar event. Triggered by "move", "reschedule", or "change time".' },
              { icon: '🧠', name: 'memory.save', desc: 'Store facts about the user — name, role, projects, preferences — after every message exchange.' },
              { icon: '📝', name: 'artifact.create', desc: 'Save generated code or documents to Artifacts for versioned access and review.' },
            ].map((t) => (
              <div key={t.name} className="flex items-start gap-3 px-5 py-3 border-b border-[#3e3f42] last:border-b-0 hover:bg-[#2b2c2f] transition-colors">
                <span className="text-xl flex-shrink-0 mt-0.5">{t.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <code className="text-[#6498CC] text-[12px] font-mono font-bold">{t.name}</code>
                    <span className="text-[10px] bg-[#1e3a5f] text-[#6498CC] font-bold px-1.5 py-0.5 rounded">ACTIVE</span>
                  </div>
                  <p className="text-[#8f9196] text-[12px] leading-relaxed">{t.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#323335] border border-[#515153] rounded-md p-4">
            <h3 className="text-white font-bold text-[14px] mb-3">Create a Tool</h3>
            <p className="text-[#8f9196] text-[12px] mb-3">
              Define a tool with a single sentence. Palinos will automatically use active tools during chat when relevant.
            </p>
            <div className="flex flex-col gap-3">
              <input
                value={newToolName}
                onChange={(e) => setNewToolName(e.target.value)}
                className="bg-[#232426] border border-[#515153] rounded px-3 py-1.5 text-white text-[13px] outline-none focus:border-[#6498CC]"
                placeholder="Tool name (e.g. web-search)"
              />
              <textarea
                value={newToolDesc}
                onChange={(e) => setNewToolDesc(e.target.value)}
                rows={3}
                className="bg-[#232426] border border-[#515153] rounded px-3 py-2 text-white text-[13px] outline-none focus:border-[#6498CC] resize-none"
                placeholder="Describe what this tool does in one sentence (Markdown supported). e.g. Search the web and return relevant results for any query."
              />
              <button
                onClick={addTool}
                disabled={!newToolName.trim() || !newToolDesc.trim()}
                className="bg-[#6498CC] hover:bg-[#5282b8] text-white px-5 py-1.5 rounded text-[13px] font-bold self-start transition-all disabled:opacity-50"
              >
                Add Tool
              </button>
            </div>
          </div>

          {tools.length > 0 && <h3 className="text-[#8f9196] text-[12px] font-semibold uppercase tracking-wider mt-2">Custom Tools</h3>}
          <div className="flex flex-col gap-3">
            {tools.length === 0 ? (
              <div className="text-[#8f9196] text-[13px] text-center py-6">No custom tools created yet.</div>
            ) : tools.map((tool) => (
              <div key={tool.id} className="bg-[#323335] border border-[#515153] rounded-md p-3 flex items-start gap-3 group hover:border-[#6498CC] transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-bold text-[13px]">{tool.name}</span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${tool.isActive ? 'bg-[#244c2a] text-[#71e184]' : 'bg-[#3e3f42] text-[#8f9196]'}`}>
                      {tool.isActive ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </div>
                  <p className="text-[#8f9196] text-[12px] leading-relaxed">{tool.description}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-all">
                  <button onClick={() => toggleTool(tool.id)} className="px-2 py-1 bg-[#232426] border border-[#515153] hover:border-[#6498CC] text-[#8f9196] hover:text-white rounded text-[11px] font-medium transition-all">
                    {tool.isActive ? 'Disable' : 'Enable'}
                  </button>
                  <button onClick={() => removeTool(tool.id)} className="px-2 py-1 bg-[#232426] border border-[#515153] hover:border-[#e17171] text-[#8f9196] hover:text-[#e17171] rounded text-[11px] font-medium transition-all">
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
